import React, { createContext, useContext, useState, ReactNode } from "react";

interface PopoverContextType {
    openPopover: string | null;
    setOpenPopover: (id: string | null) => void;
    togglePopover: (id: string) => void;
    isPopoverOpen: (id: string) => boolean;
    closeAllPopovers: () => void;
}

const PopoverContext = createContext<PopoverContextType | undefined>(undefined);

export const usePopoverManager = () => {
    const context = useContext(PopoverContext);
    if (!context) {
        throw new Error("usePopoverManager must be used within a PopoverProvider");
    }
    return context;
};

interface PopoverProviderProps {
    children: ReactNode;
}

export const PopoverProvider: React.FC<PopoverProviderProps> = ({ children }) => {
    const [openPopover, setOpenPopoverState] = useState<string | null>(null);

    const setOpenPopover = (id: string | null) => {
        setOpenPopoverState(id);
    };

    const togglePopover = (id: string) => {
        if (openPopover === id) {
            // If the same popover is clicked, close it
            setOpenPopoverState(null);
        } else {
            // If a different popover is clicked, switch to it immediately
            setOpenPopoverState(id);
        }
    };

    const isPopoverOpen = (id: string) => {
        return openPopover === id;
    };

    const closeAllPopovers = () => {
        setOpenPopoverState(null);
    };

    return (
        <PopoverContext.Provider
            value={{
                openPopover,
                setOpenPopover,
                togglePopover,
                isPopoverOpen,
                closeAllPopovers
            }}
        >
            {children}
        </PopoverContext.Provider>
    );
};
